import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { postDraftModeration } from '../api';
import { postModerationImg } from '../api/material';
import { handleSnapDiff, IEditState, useAppDispatch, useAppSelector } from '../state';
import { NpcFieldMap, NpcFieldType } from '../types';
import { EModerationImgScene, EPrivacySetting } from '../types/material';

const STATUS_CODE_SENSITIVE_INFORMATION = 1111012281;
export function useNotPassFieldToText() {
  const { t } = useTranslation();
  const getNotPassText = useCallback(
    (notPassFields: NpcFieldType[]) =>
      notPassFields.map((key) => t(NpcFieldMap[key] || 'default_npc_info')),
    [t]
  );

  return { getNotPassText };
}

export function useSingleSafeCheck() {
  const safeCheck = useCallback(
    async (checkValue: Partial<IEditState>): Promise<NpcFieldType[]> => {
      try {
        const { not_pass_field_list = [] } = await postDraftModeration({
          npc_id: checkValue.npcId!,
          setting_info: checkValue.settingInfo,
          img_info: checkValue.imgInfo,
        });
        return not_pass_field_list;
      } catch (err: any) {
        if (err.status_code === STATUS_CODE_SENSITIVE_INFORMATION) {
          return [NpcFieldType.NpcBaseImg];
        }
      }
      return [];
    },
    []
  );

  return { safeCheck };
}

export function useSafeCheck() {
  const edit = useAppSelector((state) => state.edit);
  const [lastCheckValue, setLastCheckValue] = useState<IEditState | null>(null);
  const [checking, setChecking] = useState(false);
  const [notPassRes, setNotPassRes] = useState<NpcFieldType[]>([]);
  const safeCheck = useCallback(async (): Promise<NpcFieldType[]> => {
    setChecking(true);
    const diff = handleSnapDiff(lastCheckValue, edit);
    let safeRes: NpcFieldType[] = [];
    // 若改变了重新过审，否则传上一次过审结果
    if (diff?.imgChange || diff?.settingChange || !lastCheckValue) {
      try {
        const { not_pass_field_list = [] } = await postDraftModeration({
          npc_id: edit.npcId,
          setting_info: edit.settingInfo,
          img_info: edit.imgInfo,
          plugin_list: edit.pluginList,
        });
        safeRes = not_pass_field_list;
      } catch (err: any) {
        if (err.status_code === STATUS_CODE_SENSITIVE_INFORMATION) {
          safeRes = [NpcFieldType.NpcBaseImg];
        }
      }
    } else {
      safeRes = notPassRes;
    }
    setLastCheckValue(edit);
    setChecking(false);
    setNotPassRes(safeRes);
    return safeRes;
  }, [edit, lastCheckValue, notPassRes]);

  return {
    checking,
    safeCheck,
  };
}

export function useAvatarSafeCheck() {
  const safeCheck = useCallback(
    async (imgUrl: string, scene?: EModerationImgScene): Promise<boolean[]> => {
      try {
        const res = await postModerationImg({
          img_url: [imgUrl],
          scene: scene ?? EModerationImgScene.AvatarFromPhotoAlbum,
          privacy_setting: EPrivacySetting.Public,
        });
        return res.img_safe_list || [];
      } catch (err: any) {
        console.error('avatar safe check error', err);
        return [];
      }
    },
    []
  );

  return { safeCheck };
}
